/**
 * Mock API Service for Resume AI Demo
 * Simulates API responses with demo data and realistic delays
 */

import {
    mockProfile,
    mockGeneratedResume,
    mockResumeHistory,
    mockHistoryStats,
    mockSelectionPreview,
    mockTemplates
} from '../data/mockData';

// Simulate network delay for realistic feel
const simulateDelay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms));

// Deep clone helper to avoid mutation
const clone = (obj) => JSON.parse(JSON.stringify(obj));

// Local storage key for demo data persistence
const STORAGE_KEY = 'resume_demo_profile';

// Load profile from localStorage or use mock data
const loadStoredProfile = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : clone(mockProfile);
    } catch {
        return clone(mockProfile);
    }
};

// Save profile to localStorage
const saveProfile = (profile) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch (e) {
        console.warn('Failed to save to localStorage:', e);
    }
};

// Get or initialize profile
let currentProfile = loadStoredProfile();

/**
 * Profile API endpoints
 */
export const profileApi = {
    getFullProfile: async () => {
        await simulateDelay(500);
        return { data: clone(currentProfile) };
    },

    getPersonalInfo: async () => {
        await simulateDelay(300);
        return { data: currentProfile.personalInfo };
    },

    updatePersonalInfo: async (data) => {
        await simulateDelay(400);
        currentProfile.personalInfo = { ...currentProfile.personalInfo, ...data };
        saveProfile(currentProfile);
        return { data: currentProfile.personalInfo, message: 'Personal info updated successfully' };
    },

    getCertifications: async () => {
        await simulateDelay(300);
        return { data: currentProfile.certifications };
    },

    addCertification: async (data) => {
        await simulateDelay(400);
        const newCert = { id: Date.now(), ...data };
        currentProfile.certifications.push(newCert);
        saveProfile(currentProfile);
        return { data: newCert, message: 'Certification added successfully' };
    },

    updateCertification: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.certifications.findIndex(c => c.id === parseInt(id));
        if (index !== -1) {
            currentProfile.certifications[index] = { ...currentProfile.certifications[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.certifications[index], message: 'Certification updated successfully' };
    },

    deleteCertification: async (id) => {
        await simulateDelay(300);
        currentProfile.certifications = currentProfile.certifications.filter(c => c.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Certification deleted successfully' };
    },

    getExperience: async () => {
        await simulateDelay(300);
        return { data: currentProfile.experience };
    },

    addExperience: async (data) => {
        await simulateDelay(400);
        const newExp = { id: Date.now(), ...data };
        currentProfile.experience.unshift(newExp);
        saveProfile(currentProfile);
        return { data: newExp, message: 'Experience added successfully' };
    },

    updateExperience: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.experience.findIndex(e => e.id === parseInt(id));
        if (index !== -1) {
            currentProfile.experience[index] = { ...currentProfile.experience[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.experience[index], message: 'Experience updated successfully' };
    },

    deleteExperience: async (id) => {
        await simulateDelay(300);
        currentProfile.experience = currentProfile.experience.filter(e => e.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Experience deleted successfully' };
    },

    getProjects: async () => {
        await simulateDelay(300);
        return { data: currentProfile.projects };
    },

    addProject: async (data) => {
        await simulateDelay(400);
        const newProj = { id: Date.now(), ...data };
        currentProfile.projects.unshift(newProj);
        saveProfile(currentProfile);
        return { data: newProj, message: 'Project added successfully' };
    },

    updateProject: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.projects.findIndex(p => p.id === parseInt(id));
        if (index !== -1) {
            currentProfile.projects[index] = { ...currentProfile.projects[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.projects[index], message: 'Project updated successfully' };
    },

    deleteProject: async (id) => {
        await simulateDelay(300);
        currentProfile.projects = currentProfile.projects.filter(p => p.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Project deleted successfully' };
    },

    getEducation: async () => {
        await simulateDelay(300);
        return { data: currentProfile.education };
    },

    addEducation: async (data) => {
        await simulateDelay(400);
        const newEdu = { id: Date.now(), ...data };
        currentProfile.education.unshift(newEdu);
        saveProfile(currentProfile);
        return { data: newEdu, message: 'Education added successfully' };
    },

    updateEducation: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.education.findIndex(e => e.id === parseInt(id));
        if (index !== -1) {
            currentProfile.education[index] = { ...currentProfile.education[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.education[index], message: 'Education updated successfully' };
    },

    deleteEducation: async (id) => {
        await simulateDelay(300);
        currentProfile.education = currentProfile.education.filter(e => e.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Education deleted successfully' };
    },

    getSkills: async () => {
        await simulateDelay(300);
        return { data: currentProfile.skills };
    },

    addSkill: async (data) => {
        await simulateDelay(400);
        const newSkill = { id: Date.now(), ...data };
        currentProfile.skills.push(newSkill);
        saveProfile(currentProfile);
        return { data: newSkill, message: 'Skill added successfully' };
    },

    updateSkill: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.skills.findIndex(s => s.id === parseInt(id));
        if (index !== -1) {
            currentProfile.skills[index] = { ...currentProfile.skills[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.skills[index], message: 'Skill updated successfully' };
    },

    deleteSkill: async (id) => {
        await simulateDelay(300);
        currentProfile.skills = currentProfile.skills.filter(s => s.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Skill deleted successfully' };
    },

    getAwards: async () => {
        await simulateDelay(300);
        return { data: currentProfile.awards };
    },

    addAward: async (data) => {
        await simulateDelay(400);
        const newAward = { id: Date.now(), ...data };
        currentProfile.awards.push(newAward);
        saveProfile(currentProfile);
        return { data: newAward, message: 'Award added successfully' };
    },

    updateAward: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.awards.findIndex(a => a.id === parseInt(id));
        if (index !== -1) {
            currentProfile.awards[index] = { ...currentProfile.awards[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.awards[index], message: 'Award updated successfully' };
    },

    deleteAward: async (id) => {
        await simulateDelay(300);
        currentProfile.awards = currentProfile.awards.filter(a => a.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Award deleted successfully' };
    },

    getLanguages: async () => {
        await simulateDelay(300);
        return { data: currentProfile.languages };
    },

    addLanguage: async (data) => {
        await simulateDelay(400);
        const newLang = { id: Date.now(), ...data };
        currentProfile.languages.push(newLang);
        saveProfile(currentProfile);
        return { data: newLang, message: 'Language added successfully' };
    },

    updateLanguage: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.languages.findIndex(l => l.id === parseInt(id));
        if (index !== -1) {
            currentProfile.languages[index] = { ...currentProfile.languages[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.languages[index], message: 'Language updated successfully' };
    },

    deleteLanguage: async (id) => {
        await simulateDelay(300);
        currentProfile.languages = currentProfile.languages.filter(l => l.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Language deleted successfully' };
    },

    getIndustryExperience: async () => {
        await simulateDelay(300);
        return { data: currentProfile.industryExperience };
    },

    addIndustryExperience: async (data) => {
        await simulateDelay(400);
        const newIndustry = { id: Date.now(), ...data };
        currentProfile.industryExperience.push(newIndustry);
        saveProfile(currentProfile);
        return { data: newIndustry, message: 'Industry experience added successfully' };
    },

    updateIndustryExperience: async (id, data) => {
        await simulateDelay(400);
        const index = currentProfile.industryExperience.findIndex(i => i.id === parseInt(id));
        if (index !== -1) {
            currentProfile.industryExperience[index] = { ...currentProfile.industryExperience[index], ...data };
            saveProfile(currentProfile);
        }
        return { data: currentProfile.industryExperience[index], message: 'Industry experience updated successfully' };
    },

    deleteIndustryExperience: async (id) => {
        await simulateDelay(300);
        currentProfile.industryExperience = currentProfile.industryExperience.filter(i => i.id !== parseInt(id));
        saveProfile(currentProfile);
        return { message: 'Industry experience deleted successfully' };
    },
};

/**
 * Generate a dynamic resume based on job description
 */
const generateDynamicResume = (jobDescription) => {
    // Extract keywords from job description
    const keywords = jobDescription.toLowerCase();
    const profile = currentProfile;

    // Calculate match score based on keywords found in profile
    let matchScore = 75; // Base score

    if (keywords.includes('distributed') || keywords.includes('microservices')) matchScore += 5;
    if (keywords.includes('kubernetes') || keywords.includes('k8s')) matchScore += 4;
    if (keywords.includes('aws') || keywords.includes('cloud')) matchScore += 4;
    if (keywords.includes('react') || keywords.includes('frontend')) matchScore += 3;
    if (keywords.includes('python') || keywords.includes('node')) matchScore += 3;
    if (keywords.includes('leadership') || keywords.includes('mentor')) matchScore += 3;

    matchScore = Math.min(matchScore, 95); // Cap at 95

    // Generate strengths based on profile and job
    const strengths = [
        `Strong ${profile.experience.length > 5 ? '8+' : profile.experience.length + '+'} years of hands-on engineering experience`,
        `Demonstrated expertise in ${profile.skills.slice(0, 3).map(s => s.name).join(', ')}`,
        `Track record of leading technical initiatives and mentoring teams`,
        `Relevant certifications: ${profile.certifications.slice(0, 2).map(c => c.name).join(', ')}`
    ];

    // Generate matched requirements
    const matchedRequirements = [
        "Software engineering experience requirement met",
        "Cloud platform expertise demonstrated",
        "Leadership and mentoring experience verified",
        "Technical project leadership confirmed",
        "Relevant technical stack alignment"
    ];

    // Generate gaps/recommendations
    const gaps = [
        "Consider highlighting specific achievements quantitatively",
        "Tailor project descriptions to match job requirements more closely"
    ];

    return {
        ...clone(mockGeneratedResume),
        matchScore,
        aiMatchAnalysis: {
            strengths,
            matched_requirements: matchedRequirements,
            gaps,
            recommendations: [
                "Emphasize distributed systems experience prominently",
                "Include specific metrics from recent projects",
                "Highlight relevant certifications at the top"
            ]
        }
    };
};

/**
 * Resume API endpoints
 */
export const resumeApi = {
    generateResume: async (data) => {
        await simulateDelay(2000); // Longer delay for "AI processing"
        return generateDynamicResume(data.jobDescription);
    },

    getResumes: async () => {
        await simulateDelay(500);
        return { data: clone(mockResumeHistory) };
    },

    getResume: async (id) => {
        await simulateDelay(400);
        const resume = mockResumeHistory.find(r => r.id === parseInt(id));
        return { data: resume ? { ...resume, content: mockGeneratedResume.markdown } : null };
    },

    deleteResume: async (id) => {
        await simulateDelay(300);
        return { message: 'Resume deleted successfully (demo mode)' };
    },

    generateTailored: async (jobDescription, options = {}) => {
        await simulateDelay(2500); // Longer delay for "AI processing"
        const result = generateDynamicResume(jobDescription);
        return {
            ...result,
            template: options.template || 'professional',
            aiOptimized: options.aiOptimize !== false
        };
    },

    previewSelection: async (jobDescription) => {
        await simulateDelay(1000);
        return clone(mockSelectionPreview);
    },

    generateManual: async (jobDescription, selection, options = {}) => {
        await simulateDelay(2000);
        return generateDynamicResume(jobDescription);
    },
};

/**
 * Template API endpoints
 */
export const templateApi = {
    getTemplates: async () => {
        await simulateDelay(300);
        return { data: clone(mockTemplates) };
    },

    getTemplate: async (name) => {
        await simulateDelay(300);
        const template = mockTemplates.find(t => t.name === name);
        return { data: template };
    },
};

/**
 * History API endpoints
 */
export const historyApi = {
    getHistory: async (limit = 10, offset = 0) => {
        await simulateDelay(500);
        const history = clone(mockResumeHistory);
        const paginatedHistory = history.slice(offset, offset + limit);
        return {
            data: {
                resumes: paginatedHistory,
                total: history.length
            }
        };
    },

    getResume: async (id) => {
        await simulateDelay(400);
        const resume = mockResumeHistory.find(r => r.id === parseInt(id));
        if (resume) {
            return {
                data: {
                    ...resume,
                    content: mockGeneratedResume.markdown
                }
            };
        }
        throw new Error('Resume not found');
    },

    deleteResume: async (id) => {
        await simulateDelay(300);
        return { message: 'Resume deleted successfully (demo mode - data persists for demo)' };
    },

    getStats: async () => {
        await simulateDelay(300);
        return { data: clone(mockHistoryStats) };
    },

    downloadDocx: async (id) => {
        await simulateDelay(500);
        // Create a blob with sample content for demo
        const content = `DEMO RESUME\n\n${mockGeneratedResume.markdown}\n\nThis is a demo version. In the full application, this would be a properly formatted DOCX file.`;
        return new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    },
};

/**
 * Export/Import API endpoints
 */
export const exportApi = {
    exportProfile: async () => {
        await simulateDelay(500);
        const profileData = JSON.stringify({ data: currentProfile, exportDate: new Date().toISOString() }, null, 2);
        return new Blob([profileData], { type: 'application/json' });
    },

    importProfile: async (file) => {
        await simulateDelay(800);
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const importedData = JSON.parse(event.target.result);
                    const data = importedData.data || importedData;

                    // Merge imported data with current profile
                    if (data.personalInfo) currentProfile.personalInfo = data.personalInfo;
                    if (data.certifications) currentProfile.certifications = data.certifications;
                    if (data.experience) currentProfile.experience = data.experience;
                    if (data.projects) currentProfile.projects = data.projects;
                    if (data.education) currentProfile.education = data.education;
                    if (data.skills) currentProfile.skills = data.skills;
                    if (data.awards) currentProfile.awards = data.awards;
                    if (data.languages) currentProfile.languages = data.languages;
                    if (data.industryExperience) currentProfile.industryExperience = data.industryExperience;

                    saveProfile(currentProfile);

                    resolve({
                        data: {
                            imported_count: Object.keys(data).length,
                            message: 'Profile imported successfully'
                        }
                    });
                } catch (err) {
                    reject(new Error('Invalid JSON file format'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    },
};

// Reset profile to default mock data
export const resetProfileToDefault = () => {
    currentProfile = clone(mockProfile);
    saveProfile(currentProfile);
    return currentProfile;
};
